import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import _ from 'lodash';

import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import TextArea from '../../../components/FormFields/TextArea';
import FormControl from '@material-ui/core/FormControl';
import BobConfirmButton from '../../../components/BobConfirmButton';
import BobCancelButton from '../../../components/BobCancelButton';
import BobModal from '../../../components/BobModal';

import * as ContactActions from '../../../actions/contactActions';
import * as SvcRequestActions from '../../../actions/svcRequestActions';
import * as ArchitectActions from '../../../actions/architectActions';

import * as classes from './CreateServiceRequestModal.module.css';

const ValidationSchema = Yup.object().shape({
    summary: Yup.string()
        .required(),
    description: Yup.string()
        .required(),
    priority: Yup.number()
        .required(),
    issueTypes: Yup.array()
        .required(),
    client:        Yup.string(),
    site:          Yup.string(),
    target:        Yup.string(),
    clientContact: Yup.string(),
    area:          Yup.string(),
});

const DEFAULT_FORM_VALUES = {
    summary:       '',
    description:   '',
    priority:      '',
    issueTypes:    [],
    client:        '',
    site:          '',
    target:        '',
    clientContact: '',
    area:          ''
};

const CreateServiceRequestModal = ({
    activeNavCompany,
    areas,
    contacts,
    createSvcRequest,
    currentUser,
    fetchAreas,
    fetchContacts,
    fetchIssueTypes,
    fetchSiteContacts,
    issueTypes,
    selectedHealthCheck,
    setCreateServiceRequestModalOpen,
    siteContacts,
    ...props
}) => {
    const [initialValues, setInitialValues] = useState({ ...DEFAULT_FORM_VALUES });
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const init = async () => {
            const siteId = _.get(selectedHealthCheck, 'siteId', '');
            const clientId = _.get(selectedHealthCheck, 'clientId', '');

            await fetchAreas(siteId);
            await Promise.all([
                fetchAreas(siteId),
                fetchSiteContacts(clientId, siteId)
            ].map(p => p.catch(err => err)));
        };

        if (selectedHealthCheck) {
            setIsLoading(true);
            init();
            const formObject = { ...DEFAULT_FORM_VALUES };
            const description = _.get(selectedHealthCheck, 'description', '');
            const systemEffect = _.get(selectedHealthCheck, 'systemEffect', '');
            const recommendation = _.get(selectedHealthCheck, 'recommendation', '');

            let formattedDescription = '';

            if (description && systemEffect && recommendation) {
                formattedDescription = `${description}\n\nSystem Effect: \n${systemEffect}\n\nRecommendation: \n${recommendation}`;
            } else if (description && systemEffect) {
                formattedDescription = `${description}\n\nSystem Effect: \n${systemEffect}`;
            } else if (description && recommendation) {
                formattedDescription = `${description}\n\nRecommendation: \n${recommendation}`;
            } else if (description) {
                formattedDescription = description;
            } else if (systemEffect) {
                formattedDescription = `System Effect: \n${systemEffect}`;
            } else if (recommendation) {
                formattedDescription = `Recommendation: \n${recommendation}`;
            }
            formObject.summary = _.get(selectedHealthCheck, 'name', '');
            formObject.description = formattedDescription;
            formObject.priority = _.get(selectedHealthCheck, 'priority', 0);

            const selectedIssueTypes = issueTypes.filter(({ name }) => selectedHealthCheck.issueTypes.includes(name));
            formObject.issueTypes = selectedIssueTypes;

            formObject.client = _.get(selectedHealthCheck, 'clientName', '');
            formObject.site = _.get(selectedHealthCheck, 'siteName', '');
            formObject.target = _.get(selectedHealthCheck, 'snapshotRoute', '');
            setInitialValues(formObject);
            setIsLoading(false);
        }
    }, [selectedHealthCheck]);

    const formik = useFormik({
        initialValues:      initialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {

            const formatIssueTypes = (issueTypes) => {
                return issueTypes.map(type => type.id);
            };

            let body = {
                clientId:        selectedHealthCheck.clientId,
                clientContactId: values.clientContact || null,
                companyId:       activeNavCompany.id,
                summary:         values.summary,
                description:     values.description,
                issueTypes:      formatIssueTypes(values.issueTypes),
                priority:        values.priority,
                requesterEmail:  currentUser.email,
                requesterName:   currentUser.userName,
                requesterPhone:  currentUser.phone,
                siteId:          selectedHealthCheck.siteId,
                areaId:          values.area || null,
                equipmentId:     selectedHealthCheck.equipmentId || null,
                systemId:        selectedHealthCheck.systemId || null
            };

            let isUpdated;

            if (!isLoading) {
                setIsLoading(true);
                isUpdated = await createSvcRequest(body);
            }

            if (isUpdated) {
                setIsLoading(false);
                setCreateServiceRequestModalOpen(false);
            } else {
                setIsLoading(false);
            }
        }
    });

    const MenuProps = {
        disablePortal:      true,
        getContentAnchorEl: () => null,
        PaperProps:         {
            style: {
                maxHeight: 500
            },
        },
    };

    return (
        <BobModal
            title={'Create Service Request'}
            hidebuttons={true}
            headerOverride={classes.HeaderOverride}
            isRemoveFooter={true}
            onClose={() => setCreateServiceRequestModalOpen(false)}
        >
            <div className={classes.InputColumn}>
                <div className={classes.inputs}>
                    <FormControl className={classes.InputWrapper}>
                        <TextArea
                            name="summary"
                            type="text"
                            multiline={true}
                            value={formik.values.summary}
                            label="Summary"
                            onChange={formik.handleChange}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.NameLabel
                                }
                            }}
                            InputProps={{
                                onBlur:  formik.handleBlur,
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                            FormHelperTextProps={{
                                classes: {
                                    error: classes.error
                                }
                            }}
                            error={Boolean(formik.errors.summary) && Boolean(formik.touched.summary)}
                            helperText={Boolean(formik.touched.summary) && formik.errors.summary}
                        />
                    </FormControl>
                    <FormControl className={classes.InputWrapper}>
                        <TextArea
                            name="description"
                            type="text"
                            multiline={true}
                            value={formik.values.description}
                            label="Description"
                            onChange={formik.handleChange}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.NameLabel
                                }
                            }}
                            InputProps={{
                                onBlur:  formik.handleBlur,
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                            FormHelperTextProps={{
                                classes: {
                                    error: classes.error
                                }
                            }}
                            error={Boolean(formik.errors.description) && Boolean(formik.touched.description)}
                            helperText={Boolean(formik.touched.description) && formik.errors.description}
                        />
                    </FormControl>
                    <FormControl variant="outlined" size="small" className={classes.InputWrapper}>
                        <InputLabel
                            style={{
                                marginLeft:      '-5px',
                                paddingLeft:     '5px',
                                backgroundColor: 'white',
                                paddingRight:    '5px'
                            }}
                        >
                            <span>Priority<span className={classes.Required}> *</span></span>
                        </InputLabel>
                        <Select
                            value={formik.values.priority}
                            MenuProps={MenuProps}
                            onChange={formik.handleChange}
                            className={classes.select}
                            inputProps={{
                                style: { fontSize: 14 },
                                name:  'priority',
                                id:    'priority'
                            }}
                        >
                            <MenuItem
                                className={classes.MenuItem}
                                key={0}
                                value={0}
                            >
                                Low
                            </MenuItem>
                            <MenuItem
                                className={classes.MenuItem}
                                key={1}
                                value={1}
                            >
                                Medium
                            </MenuItem>
                            <MenuItem
                                className={classes.MenuItem}
                                key={2}
                                value={2}
                            >
                                High
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" className={classes.InputWrapper}>
                        <InputLabel
                            style={{
                                marginLeft:      '-5px',
                                paddingLeft:     '5px',
                                backgroundColor: 'white',
                                paddingRight:    '5px'
                            }}
                        >
                            <span>Issue Types<span className={classes.Required}> *</span></span>
                        </InputLabel>
                        <Select
                            value={formik.values.issueTypes}
                            classes={{ root: classes.SelectFont }}
                            MenuProps={MenuProps}
                            multiple
                            renderValue={(selected) => selected.map(item => item.name).join(', ')}
                            onChange={formik.handleChange}
                            className={classes.select}
                            inputProps={{
                                style: { fontSize: 16 },
                                name:  'issueTypes',
                                id:    'issueTypes'
                            }}
                        >
                            <MenuItem value="none" disabled>
                                Select Issue Type
                            </MenuItem>
                            {issueTypes.map((type, index) => (
                                <MenuItem key={index} value={type}>
                                    <Checkbox style={{ color: 'black' }} checked={formik.values.issueTypes.indexOf(type) > -1} />
                                    <ListItemText className={classes.MenuItem} primary={type.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" className={classes.InputWrapper}>
                        <TextField
                            id="client"
                            disabled={true}
                            label={<span>Client<span className={classes.Required}> *</span></span>}
                            placeholder="Enter"
                            value={formik.values.client}
                            onChange={formik.handleChange}
                            size="small"
                            inputProps={{
                                style: { fontSize: 14 }
                            }}
                            InputLabelProps={{
                                className: classes.NameLabel
                            }}
                            className={classes.NameInput}
                            variant="outlined"
                        />
                    </FormControl>
                    <FormControl variant="outlined" size="small" className={classes.InputWrapper}>
                        <TextField
                            id="site"
                            disabled={true}
                            label={<span>Site<span className={classes.Required}> *</span></span>}
                            placeholder="Enter"
                            value={formik.values.site}
                            onChange={formik.handleChange}
                            size="small"
                            inputProps={{
                                style: { fontSize: 14 }
                            }}
                            InputLabelProps={{
                                className: classes.NameLabel
                            }}
                            className={classes.NameInput}
                            variant="outlined"
                        />
                    </FormControl>
                    <FormControl variant="outlined" size="small" className={classes.InputWrapper}>
                        <TextField
                            id="target"
                            disabled={true}
                            label={<span>Target<span className={classes.Required}> *</span></span>}
                            placeholder="Enter"
                            value={formik.values.target}
                            onChange={formik.handleChange}
                            size="small"
                            inputProps={{
                                style: { fontSize: 14 }
                            }}
                            InputLabelProps={{
                                className: classes.NameLabel
                            }}
                            className={classes.NameInput}
                            variant="outlined"
                        />
                    </FormControl>
                    <FormControl variant="outlined" size="small" className={classes.InputWrapper}>
                        <InputLabel
                            style={{
                                marginLeft:      '-5px',
                                paddingLeft:     '5px',
                                backgroundColor: 'white',
                                paddingRight:    '5px'
                            }}
                        >
                            <span>Site Contact</span>
                        </InputLabel>
                        <Select
                            value={formik.values.clientContact}
                            MenuProps={MenuProps}
                            onChange={formik.handleChange}
                            className={classes.select}
                            inputProps={{
                                style: { fontSize: 14 },
                                name:  'clientContact',
                                id:    'clientContact'
                            }}
                        >
                            {
                                siteContacts.map((contact, index) => (
                                    <MenuItem
                                        className={classes.MenuItem}
                                        value={contact.id}
                                        key={index}
                                    >
                                        {`${contact.userName.FirstName} ${contact.userName.LastName}`}
                                    </MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" className={classes.InputWrapper}>
                        <InputLabel
                            style={{
                                marginLeft:      '-5px',
                                paddingLeft:     '5px',
                                backgroundColor: 'white',
                                paddingRight:    '5px'
                            }}
                        >
                            <span>Area</span>
                        </InputLabel>
                        <Select
                            value={formik.values.area}
                            classes={{ root: classes.SelectFont }}
                            MenuProps={MenuProps}
                            onChange={formik.handleChange}
                            className={classes.select}
                            inputProps={{
                                style: { fontSize: 14 },
                                name:  'area',
                                id:    'area'
                            }}
                        >
                            <MenuItem value="none" disabled>
                                Select Area
                            </MenuItem>
                            {areas.map((area, index) => (
                                <MenuItem
                                    className={classes.MenuItem}
                                    key={index}
                                    value={area.id}
                                >
                                    {area.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <div className={classes.ButtonContainer}>
                    <BobCancelButton
                        onClick={() => setCreateServiceRequestModalOpen(false)}
                        className={classes.CancelButton}
                        label={'Close'}
                    />
                    <BobConfirmButton
                        onClick={formik.handleSubmit}
                        className={classes.Buttons}
                        label={'Create'}
                        isLoading={isLoading}
                        iconSize={15}
                    />
                </div>
            </div>
        </BobModal>
    );
};

const mapStateToProps = state => {
    return {
        activeNavCompany: state.bob.activeNavCompany,
        areas:            state.architect.areas,
        siteContacts:     state.contact.siteContacts,
        contacts:         state.architect.contacts,
        currentUser:      state.currentUser,
        issueTypes:       state.svcManagement.issueTypes
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ContactActions,
    ...SvcRequestActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateServiceRequestModal);
